import compose from '@hypnosphi/recompose/compose';
import { withStyles } from '@mui/styles';

import Step from 'apollo-react/components/Step';
import StepLabel from 'apollo-react/components/StepLabel';
import Stepper from 'apollo-react/components/Stepper';

import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostmongerContext } from '../../contexts/PostmongerContext';

import Autocomplete from 'apollo-react/components/Autocomplete';
import Link from 'apollo-react/components/Link';
import MenuItem from 'apollo-react/components/MenuItem';
import Select from 'apollo-react/components/Select';
import TextField from 'apollo-react/components/TextField';
import Tooltip from 'apollo-react/components/Tooltip';
import InfoIcon from 'apollo-react-icons/Info';
import IconButton from 'apollo-react/components/IconButton';
import Download from 'apollo-react-icons/Download';
import { Alert } from 'reactstrap';

const styles = {
    section: {
      marginBottom: 32,
    },
    antiSection: {
      marginTop: -16,
      marginBottom: 16,
    },
  };

// FIELDS USED IN inArguments
const fieldNames = {
  profile: 'ftp',
  channel: 'channelCode',
  callToAction: 'callToAction',
  tacticKey: 'tacticKey',
  fileLayout: 'fileLayout',
  filename: 'filename',
  scheduleDates: 'scheduleDates',
  timezone: 'timezone',
  time: 'time',
};

export const FTPFileUpload = (props, ref) => {
  const { t } = useTranslation();
  const previewFileDownloadUrl = 'api/1.0/SeedData/ftpPreviewFile/{businessUnitMID}/{topicName}/{channelCode}/{isHco}';

  useImperativeHandle(ref, _ => ({ isValid: page => isValid(page) }), []);  

  const { classes, page } = props;
  const { inArguments, seedData, setDirty, disabled, dataExtensionId } = usePostmongerContext();
  const { profiles, channels, callsToAction, fileLayouts, timezones, times, days, segments, maxRecordCounts, defaultTimezone } = (seedData && seedData.FTPFileUpload) ? seedData.FTPFileUpload : { };

  const availableProfiles = profiles || [];
  const availableChannels = channels || [];
  const availableFileLayouts = fileLayouts || [];
  const availableCallsToAction = callsToAction || [];
  const availableTimezones = timezones || [];
  const availableTimes = times || [];
  const availableDays = days || [];
  const availableSegments = segments || [];
  const availableMaxRecordCounts = maxRecordCounts || [];

  const deliveryScheduleMenuItems = availableDays
    .map(day => (<MenuItem value={+(day.Code)} key={day.Code}>{day.Description}</MenuItem>));

  const timezonesMenuItems = availableTimezones
    .map(timezone => (<MenuItem key={timezone.Code} value={timezone.Code}>{timezone.Description}</MenuItem>));

  const timesMenuItems = availableTimes
    .map(time => (<MenuItem key={time.Code} value={time.Code}>{time.Description}</MenuItem>));

  const fileLayoutMenuItems = availableFileLayouts
    .sort((a, b) => a.ProfileName.localeCompare(b.ProfileName))
    .map(layout => (<MenuItem key={layout.Id} value={layout.Id}>{layout.ProfileName}</MenuItem>));

  const profilesMenuItems = availableProfiles
    .sort((a, b) => a.Name.localeCompare(b.Name))
    .map(profile => (<MenuItem key={profile.Id} value={profile.Id}>{profile.Name}</MenuItem>));

  const channelsMenuItems = [
    <MenuItem key={'None'} value={'None'}>{t('generic.none')}</MenuItem>,
    ...availableChannels
    .sort((a, b) => a.Description.localeCompare(b.Description))
    .map(channel => (
      <MenuItem key={channel.Code} value={channel.Code}>
        {channel.Description}
      </MenuItem>
    )),
  ];

  const segmentsMenuItems = availableSegments
    .map(segment => ({ label: segment.Description, value: segment.Code }));

  const findSelectedSegment = dataExtensionId && 
    dataExtensionId !== '' ? 
    [segmentsMenuItems.find(segmentation => segmentation['value'] === dataExtensionId)] : [];

  const selectedSegment = findSelectedSegment && 
    findSelectedSegment.length > 0 &&
    findSelectedSegment[0] ? [findSelectedSegment[0]['value']] : [];
  
  const callToActionMenuItems = [
    ...availableCallsToAction
    .sort((a, b) => a.Description.localeCompare(b.Description))
    .map(action => (
      <MenuItem key={action.Code} value={action.Code}>
        {action.Description}
      </MenuItem>
    )),
  ]; 

  const DEFAULT_PREVIEW_FILE_MAX_RECORD_COUNT = 10;
  const maxRecordCountsItems = availableMaxRecordCounts
    .map(count => (<MenuItem key={count.Code} value={count.Code}>{count.Description}</MenuItem>));

  const [state, _setState] = useState({});
  const [invalidKey, setInvalidKey] = useState(0); // trick to force update on button click validation
  const [channel, setChannel] = useState(inArguments.get(fieldNames.channel));
  const [callToAction, setcallToAction] = useState(inArguments.get(fieldNames.callToAction));
  const [tacticKey, setTacticKey] = useState(inArguments.get(fieldNames.tacticKey));
  const [profile, setProfile] = useState(inArguments.get(fieldNames.profile));
  const [fileLayout, setFileLayout] = useState(inArguments.get(fieldNames.fileLayout));
  const [filename, setFilename] = useState(inArguments.get(fieldNames.filename));
  const [deliverySchedule, setDeliverySchedule] = useState(inArguments.get(fieldNames.scheduleDates) || []);
  const [timezone, setTimezone] = useState(inArguments.get(fieldNames.timezone) || defaultTimezone);
  const [time, setTime] = useState(inArguments.get(fieldNames.time));
  const [maxRecordCount, setMaxRecordCount] = useState(DEFAULT_PREVIEW_FILE_MAX_RECORD_COUNT);
  const [segment, setSegment] = useState(selectedSegment || {});

  const validateField = (key, value) => {
    state[key] = value ? undefined : t('generic.required');
    return state[key] ? 1 : 0;
  }

  const setFieldValue = (key, value) => {
    //If value was not selected, need to send a blank, unless it's the channel, then send 'None'
    if (inArguments.get(key) == undefined)
    {
      inArguments.set(key, value);
    } 
  }

  //if we've got a default Timezone, update our inArguments as well.
  if (!inArguments.get(fieldNames.timezone) && defaultTimezone)
  {
    setFieldValue(fieldNames.timezone, defaultTimezone);
  }

  const onChange = (set, key) => event => {
    validateField(key, event.target.value);
    set(event.target.value);
    inArguments.set(key, event.target.value);
    if (key === fieldNames.fileLayout) {
      const layout = availableFileLayouts.find(layout => layout.Id === event.target.value);
      if (layout && layout.DefaultFileName) {
        onChange(setFilename, fieldNames.filename)({ target: { value: layout.DefaultFileName } });
      }
    }
    setDirty();
  }

  const onSegmentChange = (event, segment) => {
    setSegment(segment);
    setDirty();
  }

  const isValid = page => {
    let key = 0;

    if (page == 0)
    {
      //We only come here if we've already got a channel selected.  Verify it's still an active channel.
      //Do we have an existing channel?  Validate to check if channel still exists or status was Inactivated.
      if (!disabled && inArguments.get(fieldNames.channel) && inArguments.get(fieldNames.channel) !== 'None')
      {
        if (!availableChannels.find(channel => channel.Code == inArguments.get(fieldNames.channel)))
        {
          inArguments.set(fieldNames.channel, undefined);
          state[fieldNames.channel] = t('generic.inactiveChannelSelected');
          key += 1;
        }
      }
    }

    if (page > 0) // always true :) but reads better
    {
      // Validate page 1 fields only if page 1 was reached

      setFieldValue(fieldNames.callToAction, '');
      setFieldValue(fieldNames.tacticKey, '');
      
      key += 2**0 * validateField(fieldNames.channel, inArguments.get(fieldNames.channel));
      key += 2**3 * validateField(fieldNames.profile, inArguments.get(fieldNames.profile));
      key += 2**4 * validateField(fieldNames.fileLayout, inArguments.get(fieldNames.fileLayout));

      // validate filename ( required + [a-zA-Z0-9-_])
      const filename = inArguments.get(fieldNames.filename);
      key += 2**5 * validateField(fieldNames.filename, filename);

      if (state[fieldNames.filename] == undefined && !/^[a-zA-Z0-9-_$.]+$/.test(filename)) {
        state[fieldNames.filename] = t('ftpFileUpload.fileNameCanOnlyContainValuesCharAndNumbers');
        key += 2**6;
      }
    }

    if (page > 1) {
      // Validate page 2 fields only if page 2 was reached

      const schedule = inArguments.get(fieldNames.scheduleDates);

      if (!schedule || schedule.length === 0) {
        state[fieldNames.scheduleDates] = t('ftpFileUpload.selectDayValidation');
        key += 2**7;
      } else {
        state[fieldNames.scheduleDates] = undefined;
      }

      key += 2**8 * validateField(fieldNames.timezone, inArguments.get(fieldNames.timezone));
      key += 2**9 * validateField(fieldNames.time, inArguments.get(fieldNames.time));
    }

    setInvalidKey(key);
    return key === 0;
  };

  const queryString = (url = '', requestParams = {}) =>
  `${url}?${Object.keys(requestParams)
    .map(param => `${param}=${encodeURIComponent(requestParams[param])}`)
    .join('&')}`;

  const getPreviewFileDownloadUrl = () => {
    if (!(seedData.Metadata.TopicName && seedData.Metadata.BusinessUnitMID && fileLayout && segment && segment.value && maxRecordCount)) {
      return '';
    }

    const url = previewFileDownloadUrl
      .replace('{businessUnitMID}', String(seedData.Metadata.BusinessUnitMID))
      .replace('{topicName}', encodeURIComponent(seedData.Metadata.TopicName))
      .replace('{isHco}',seedData?.isHCOEnabled && seedData?.Metadata?.IsHCO)
      .replace('{channelCode}', channel && channel !== '' ? channel : 'None');

    const params = {
      fileLayoutId: fileLayout,
      filename,
      segmentName: segments.find(segmentation => segmentation['Code'] === segment.value)['Description'],
      maxRecordCount,
      tacticKey: tacticKey || '',
    };

    return queryString(url, params);
  };

  return (
    <>
      <Stepper activeStep={page-1} alternativeLabel className={classes.antiSection}>
        <Step key={1}>
          <StepLabel>
            {t('ftpFileUpload.information')}
          </StepLabel>
        </Step>
        <Step key={2}>
          <StepLabel>
            {t('ftpFileUpload.scheduling')}
          </StepLabel>
        </Step>
        <Step key={3}>
          <StepLabel>
            {t('ftpFileUpload.preview')}
          </StepLabel>
        </Step>
      </Stepper>

      {page === 1 && (
        <div className={classes.section}>
          {disabled && <TextField
            fullWidth 
            label={t('ftpFileUpload.consentCaption')} 
            value={channel}
            disabled={disabled}
          />}
          {!disabled && <Tooltip title={t('ftpFileUpload.consentTooltip')}>
            <Select
              data-testid="channels"
              name={fieldNames.channel}
              label={t('ftpFileUpload.consentCaption')}
              value={channel}
              fullWidth
              required
              canDeselect={true}
              disabled={disabled}
              onChange={ onChange(setChannel, fieldNames.channel) }
              error={ !!state[fieldNames.channel] }   
              helperText={ state[fieldNames.channel] }   
            >
              {channelsMenuItems}
            </Select>
          </Tooltip>}
          {channel === 'None' && ( <Alert severity={'error'} color={'red'} style={{backgroundColor: 'rgb(255, 213, 153)'}}>
            {t('ftpFileUpload.NoChannelWarning')}
          </Alert>)
          }
          <Tooltip title={t('ftpFileUpload.callToActionTooltip')}>
            <Select
              name={fieldNames.callToAction}
              label={t('ftpFileUpload.callToActionCaption')}
              value={callToAction}
              fullWidth
              canDeselect={true}
              disabled={disabled}
              onChange={ onChange(setcallToAction, fieldNames.callToAction) }
              error={ !!state[fieldNames.callToAction] }   
              helperText={ state[fieldNames.callToAction] }   
            >
              {callToActionMenuItems}
            </Select>
          </Tooltip>

          <Tooltip title={t('ftpFileUpload.tacticKeyTooltip')}>
            <TextField
              fullWidth 
              label={t('ftpFileUpload.tacticKeyCaption')} 
              value={tacticKey}
              disabled={disabled}
              onChange={ onChange(setTacticKey, fieldNames.tacticKey) }
            />
          </Tooltip>

          <Tooltip title={t('ftpFileUpload.profileTooltip')}>
            <Select
              name={fieldNames.profile}
              label={t('ftpFileUpload.profileCaption')}
              value={profile}
              fullWidth
              canDeselect={false}
              disabled={disabled}
              required
              onChange={ onChange(setProfile, fieldNames.profile) }
              error={ !!state[fieldNames.profile] }   
              helperText={ state[fieldNames.profile] }   
            >
              {profilesMenuItems}
            </Select>
          </Tooltip>

          <Tooltip title={t('ftpFileUpload.fileLayoutTooltip')}>
            <Select
              data-testid="fileLayout"
              name={fieldNames.fileLayout}
              label={t('ftpFileUpload.fileLayoutCaption')}
              value={fileLayout}
              fullWidth
              required
              canDeselect={false}
              disabled={disabled}
              onChange={ onChange(setFileLayout, fieldNames.fileLayout) }
              error={ !!state[fieldNames.fileLayout] }   
              helperText={ state[fieldNames.fileLayout] }   
            >
              {fileLayoutMenuItems}
            </Select>
          </Tooltip> 

          <Tooltip title={t('ftpFileUpload.filenameTooltip')}>
            <TextField
              data-testid="filename"
              fullWidth 
              label={t('ftpFileUpload.filenameCaption')} 
              value={filename}
              disabled={disabled}
              required
              onChange={ onChange(setFilename, fieldNames.filename) }
              error={ !!state[fieldNames.filename] }   
              helperText={ state[fieldNames.filename] }   
            />
          </Tooltip>
        </div>
      )}

      {page === 2 && (
        <div className={classes.section}>
          <Tooltip title={t('ftpFileUpload.deliveryScheduleTooltip')}>
            {}
            <Select
              label={t('ftpFileUpload.deliveryScheduleCaption')}
              helperText={ state[fieldNames.scheduleDates] } 
              value={deliverySchedule}
              onChange={ onChange(setDeliverySchedule, fieldNames.scheduleDates) }
              placeholder={t('ftpFileUpload.deliveryPlaceholder')} 
              multiple
              fullWidth
              disabled={disabled}
              required
              error={ !!state[fieldNames.scheduleDates] }   
            >
              {deliveryScheduleMenuItems}  
            </Select>
          </Tooltip>

          <Tooltip title={t('ftpFileUpload.timezoneTooltip')}>
            <Select
                name={fieldNames.timezone}
                label={t('ftpFileUpload.timezoneCaption')}
                value={timezone}
                fullWidth
                required
                canDeselect={false}
                disabled={disabled}
                onChange={ onChange(setTimezone, fieldNames.timezone) }
                error={ !!state[fieldNames.timezone] }   
                helperText={ state[fieldNames.timezone] }   
              >
              {timezonesMenuItems}
            </Select>
          </Tooltip>

          <Tooltip title={t('ftpFileUpload.timeTooltip')}>
            <Select
                name={fieldNames.time}
                label={t('ftpFileUpload.timeCaption')}
                value={time}
                fullWidth
                required
                canDeselect={false}
                disabled={disabled}
                onChange={ onChange(setTime, fieldNames.time) }
                error={ !!state[fieldNames.time] }   
                helperText={ state[fieldNames.time] }   
              >
              {timesMenuItems}
            </Select>
          </Tooltip>        
        </div>
      )} 

      {page === 3 && (
        <div className={classes.section}>
          <Autocomplete
            label={<React.Fragment>{t('ftpFileUpload.segmentToPreview')} 
            <Tooltip variant="dark" title={t('ftpFileUpload.segmentInfo')} placement="bottom" style={{ marginRight: 48 }}>
            <IconButton color="primary">
              <InfoIcon />
            </IconButton>
            </Tooltip></React.Fragment>}
            source={segmentsMenuItems}
            fullWidth
            singleSelect
            value={segment}
            disabled={disabled}
            onChange={ onSegmentChange }
          />
          <Select
              data-testid="maxRecordCount"
              name='maxRecordCount'
              label={t('ftpFileUpload.maxRecordCount')}
              fullWidth
              onChange={ onChange(setMaxRecordCount, 'maxRecordCount') }
              value={maxRecordCounts ? maxRecordCount : ''}
              canDeselect={false}
              disabled={disabled}
              error={ !!state[maxRecordCount] }   
              helperText={ state[maxRecordCount] } 
            >
            {maxRecordCountsItems}
          </Select>
          <Link  
            data-testid="previewUrl"
            disabled={!(fileLayout && filename && segment && segment.value && maxRecordCount)}
            rel="noopener noreferrer"
            href={getPreviewFileDownloadUrl()}
            target="_blank"
          >
            <Download />
            {t('ftpFileUpload.download')}
          </Link>
        </div>
      )}
    </>
  )
};

export default compose(
    withStyles(styles)
    )(forwardRef(FTPFileUpload));